import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LocationMarkerIcon, 
  ChartBarIcon, 
  EyeIcon, 
  ArrowRightIcon,
  LightningBoltIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  UserGroupIcon
} from '@heroicons/react/outline';

const NetworkIntroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [direction, setDirection] = useState(1); // 1 for right, -1 for left
  
  // Sites carousel data
  const carouselSites = [
    {
      image: "/assets/site_carrefour1.jpg",
      title: "Centres Commerciaux Premium",
      description: "Captez l'attention aux points d'achat stratégiques",
      stats: [
        { value: "+30K", label: "Visiteurs/Jour" },
        { value: "92%", label: "Mémorisation" }
      ],
      color: "#E84E0E"
    },
    {
      image: "/assets/site_4.jpg",
      title: "Artères Urbaines Principales",
      description: "Maximisez votre visibilité dans les zones à fort trafic",
      stats: [
        { value: "+45K", label: "Passages/Jour" },
        { value: "24/7", label: "Diffusion" }
      ],
      color: "#3B82F6"
    },
    {
      image: "/assets/site_carrefour2.jpg",
      title: "Espaces Business & Corporatifs",
      description: "Touchez les décideurs et professionnels influents",
      stats: [
        { value: "+15K", label: "Cadres/Jour" },
        { value: "85%", label: "Taux d'audience" }
      ],
      color: "#10B981"
    }
  ];

  // Handle autoplay
  useEffect(() => {
    let interval;
    if (autoplay) {
      interval = setInterval(() => {
        setDirection(1);
        setCurrentSlide((prev) => (prev === carouselSites.length - 1 ? 0 : prev + 1));
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoplay, carouselSites.length]);
  
  // Handlers for navigation
  const nextSlide = () => {
    setAutoplay(false);
    setDirection(1);
    setCurrentSlide((prev) => (prev === carouselSites.length - 1 ? 0 : prev + 1));
  };
  
  const prevSlide = () => {
    setAutoplay(false);
    setDirection(-1);
    setCurrentSlide((prev) => (prev === 0 ? carouselSites.length - 1 : prev - 1));
  };

  // Animation variants
  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      transition: {
        duration: 0.6
      }
    })
  };

  // Slide content animation variants
  const slideContentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: delay * 0.1 + 0.3,
        duration: 0.6
      }
    })
  };

  // Key metrics data
  const metrics = [
    { icon: <LocationMarkerIcon className="h-6 w-6" />, value: "100+", label: "Sites premium" },
    { icon: <ChartBarIcon className="h-6 w-6" />, value: "2M+", label: "Audience quotidienne" },
    { icon: <UserGroupIcon className="h-6 w-6" />, value: "15+", label: "Villes couvertes" },
    { icon: <LightningBoltIcon className="h-6 w-6" />, value: "24h", label: "Activation rapide" }
  ];

  return (
    <section className="relative min-h-[600px] bg-gradient-to-br from-gray-900 to-black overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0">
        {/* Particle effect */}
        {Array.from({ length: 15 }).map((_, index) => (
          <motion.div
            key={index}
            className="absolute rounded-full bg-[#E84E0E]"
            style={{
              width: Math.random() * 8 + 4 + 'px',
              height: Math.random() * 8 + 4 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              opacity: Math.random() > 0.5 ? 0.2 : 0.1,
            }}
            animate={{
              y: [0, Math.random() * -80 - 50],
              x: [0, Math.random() * 50 - 25],
              opacity: [0.1, 0.2, 0],
              scale: [1, Math.random() * 0.5 + 1, 0],
            }}
            transition={{
              duration: Math.random() * 10 + 15,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 5,
            }}
          />
        ))}
        
        {/* Gradient orbs - color changes based on current slide */}
        <motion.div 
          className="absolute -top-40 right-20 w-[600px] h-[600px] rounded-full opacity-10"
          animate={{
            background: `radial-gradient(circle, ${carouselSites[currentSlide].color}CC 0%, ${carouselSites[currentSlide].color}00 70%)`,
            scale: [1, 1.1, 1],
            y: [0, 20, 0],
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            background: { duration: 0.8 }
          }}
        />
        
        <motion.div 
          className="absolute bottom-20 -left-40 w-[500px] h-[500px] rounded-full opacity-10"
          animate={{
            background: `radial-gradient(circle, ${carouselSites[currentSlide].color}99 0%, ${carouselSites[currentSlide].color}00 70%)`,
            scale: [1, 1.2, 1],
            y: [0, -20, 0],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            background: { duration: 0.8 }
          }}
        />
      </div>
      
      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left content - Text */}
          <motion.div 
            className="lg:col-span-5 text-white"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            <motion.div 
              variants={fadeInUpVariants}
              className="inline-flex px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6"
            >
              <span className="text-white font-medium">NOTRE CEINTURE DIGITALE</span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUpVariants}
              className="text-4xl md:text-5xl font-bold leading-tight mb-6"
            >
              Des écrans stratégiques pour un 
              <span className="relative ml-2">
                <span className="relative z-10">impact maximum</span>
                <motion.div 
                  className="absolute -bottom-2 w-full left-0 h-2"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                >
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 20" preserveAspectRatio="none">
                    <motion.path 
                      d="M0,10 Q30,20 50,10 T100,10" 
                      stroke={carouselSites[currentSlide].color}
                      strokeOpacity={0.6}
                      strokeWidth="6" 
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ 
                        pathLength: 1,
                        stroke: carouselSites[currentSlide].color 
                      }}
                      transition={{ 
                        delay: 0.9, 
                        duration: 1,
                        stroke: { duration: 0.8 }
                      }}
                    />
                  </svg>
                </motion.div>
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUpVariants}
              className="text-white/80 text-lg mb-8 max-w-xl"
            >
              Notre réseau d'affichage digital couvre les zones stratégiques à fort trafic. Sélectionnez les sites parfaits pour votre campagne et touchez votre audience cible au moment opportun.
            </motion.p>
            
            <motion.div 
              variants={fadeInUpVariants}
              className="flex flex-wrap gap-4 mb-10"
            >
              <motion.a
                href="#explorer"
                className="bg-[#E84E0E] text-white px-6 py-3 rounded-full font-medium flex items-center gap-2 shadow-lg"
                whileHover={{ scale: 1.05, boxShadow: "0 15px 30px -5px rgba(232, 78, 14, 0.4)" }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Explorer les sites</span>
                <ChevronRightIcon className="h-5 w-5" />
              </motion.a>
              
              <motion.a
                href="#packages"
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30 px-6 py-3 rounded-full font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Voir les packages
              </motion.a>
            </motion.div>
            
            {/* Key metrics */}
            <motion.div 
              variants={fadeInUpVariants}
              className="grid grid-cols-2 gap-4"
            >
              {metrics.map((metric, index) => (
                <motion.div 
                  key={index} 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 flex items-center"
                  whileHover={{ y: -5, backgroundColor: "rgba(255, 255, 255, 0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center mr-3" 
                    style={{ backgroundColor: carouselSites[currentSlide].color }}
                  >
                    {metric.icon}
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white">{metric.value}</div>
                    <div className="text-white/70 text-sm">{metric.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Right content - Site Carousel */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative">
              {/* Carousel container with navigation */}
              <div className="relative overflow-hidden rounded-2xl shadow-2xl border border-white/20 bg-gray-900">
                {/* Navigation arrows */}
                <div className="absolute inset-0 flex items-center justify-between px-4 z-10">
                  <motion.button
                    onClick={prevSlide}
                    className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white border border-white/20"
                    whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.5)" }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ChevronLeftIcon className="h-6 w-6" />
                  </motion.button>
                  
                  <motion.button
                    onClick={nextSlide}
                    className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white border border-white/20"
                    whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.5)" }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ChevronRightIcon className="h-6 w-6" />
                  </motion.button>
                </div>
                
                {/* Carousel slides */}
                <div className="h-[500px] w-full">
                  <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                      key={currentSlide}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="absolute inset-0 w-full h-full"
                    >
                      <div 
                        className="w-full h-full bg-cover bg-center" 
                        style={{ backgroundImage: `url(${carouselSites[currentSlide].image})` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/20"></div>
                        
                        {/* Slide content */}
                        <div className="absolute inset-0 flex flex-col justify-end p-8">
                          <motion.div
                            initial="hidden"
                            animate="visible"
                            exit={{ opacity: 0, y: 10 }}
                          >
                            <motion.div 
                              className="inline-block px-3 py-1 rounded-lg text-sm font-medium mb-3"
                              style={{ backgroundColor: carouselSites[currentSlide].color }}
                              custom={1}
                              variants={slideContentVariants}
                            >
                              ÉCRANS {currentSlide + 1}/{carouselSites.length}
                            </motion.div>
                            
                            <motion.h2 
                              className="text-3xl font-bold mb-3 text-white"
                              custom={2}
                              variants={slideContentVariants}
                            >
                              {carouselSites[currentSlide].title}
                            </motion.h2>
                            
                            <motion.p 
                              className="text-white/80 mb-5 max-w-xl"
                              custom={3}
                              variants={slideContentVariants}
                            >
                              {carouselSites[currentSlide].description}
                            </motion.p>
                            <motion.div 
                              className="text-white/80 mb-5 max-w-xl"
                              custom={3}
                              variants={slideContentVariants}
                            >
                              {carouselSites[currentSlide].description}
                            </motion.div>
                            
                            {/* Site stats */}
                            <motion.div 
                              className="flex space-x-4 items-center"
                              custom={4}
                              variants={slideContentVariants}
                            >
                              {carouselSites[currentSlide].stats.map((stat, idx) => (
                                <div key={idx} className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-2 border border-white/10">
                                  <motion.span
                                    className="text-xl font-bold"
                                    style={{ color: carouselSites[currentSlide].color }}
                                  >
                                    {stat.value}
                                  </motion.span>
                                  <span className="text-white/80 text-sm">{stat.label}</span>
                                </div>
                              ))}
                              
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white py-2 px-4 rounded-lg border border-white/20 flex items-center space-x-2"
                              >
                                <EyeIcon className="h-5 w-5" />
                                <span>Voir détails</span>
                              </motion.button>
                            </motion.div>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
                
                {/* Carousel indicators */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                  {carouselSites.map((_, index) => (
                    <motion.button
                      key={index}
                      onClick={() => {
                        setDirection(index > currentSlide ? 1 : -1);
                        setCurrentSlide(index);
                        setAutoplay(false);
                      }}
                      className={`w-2.5 h-2.5 rounded-full ${
                        index === currentSlide ? 'bg-white' : 'bg-white/40'
                      }`}
                      whileHover={{ scale: 1.5 }}
                      whileTap={{ scale: 0.9 }}
                      animate={index === currentSlide ? {
                        scale: [1, 1.2, 1],
                        transition: { duration: 1, repeat: Infinity }
                      } : {}}
                    />
                  ))}
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -left-3 -top-3 w-24 h-24">
                  <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <motion.circle 
                      cx="50" 
                      cy="50" 
                      r="40" 
                      stroke={carouselSites[currentSlide].color} 
                      strokeWidth="2" 
                      strokeDasharray="60 30"
                      animate={{ rotate: 360 }} 
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    />
                  </svg>
                </div>
                
                <div className="absolute -right-3 -bottom-3 w-12 h-12">
                  <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <motion.rect 
                      x="10" 
                      y="10" 
                      width="30" 
                      height="30" 
                      stroke={carouselSites[currentSlide].color} 
                      strokeWidth="2" 
                      rx="8"
                      animate={{ rotate: -360, scale: [1, 0.9, 1] }} 
                      transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    />
                  </svg>
                </div>
              </div>
              
              {/* Decorative floating card */}
              <motion.div
                className="absolute -right-20 top-1/3 bg-white/10 backdrop-blur-lg rounded-xl p-4 shadow-lg border border-white/20 max-w-xs hidden md:block"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0, y: [0, -10, 0] }}
                transition={{ 
                  opacity: { duration: 0.8, delay: 1 },
                  x: { duration: 0.8, delay: 1 },
                  y: { duration: 4, repeat: Infinity, repeatType: "reverse" }
                }}
              >
                <div className="flex items-start">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: carouselSites[currentSlide].color }}>
                    <ChartBarIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">Impact immédiat</h4>
                    <p className="text-white/70 text-sm">Nos écrans génèrent une mémorisation de marque 45% supérieure aux médias traditionnels.</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white flex flex-col items-center cursor-pointer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ 
            opacity: { duration: 0.8, delay: 1.5 },
            y: { duration: 1.5, repeat: Infinity, repeatType: "reverse" }
          }}
          onClick={() => {
            document.getElementById('explorer').scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <span className="text-sm mb-2">Découvrir plus</span>
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <motion.div 
              className="w-1.5 h-1.5 bg-white rounded-full"
              animate={{ y: [0, 15, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NetworkIntroSection;